import { useState } from 'react';
import { Box, Button, TextField, Typography, Alert, Link } from '@mui/material';
import { useAuth } from '../../../app/providers/AuthProvider';

type PasswordResetFormProps = {
  readonly onSwitchToSignIn: () => void;
  readonly onClose: () => void;
};

const PasswordResetForm = ({ onSwitchToSignIn, onClose }: PasswordResetFormProps) => {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await resetPassword(email);
      setSuccess(true);
      // Auto-close after 5 seconds
      setTimeout(() => {
        onClose();
      }, 5000);
    } catch (err) {
      const error = err as { code?: string; message: string };

      switch (error.code) {
        case 'auth/user-not-found':
          setError('No account found with this email');
          break;
        case 'auth/invalid-email':
          setError('Invalid email address');
          break;
        default:
          setError(error.message || 'Failed to send reset email');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Check your email
        </Typography>
        <Alert severity="success" sx={{ mt: 2 }}>
          Password reset instructions have been sent to <strong>{email}</strong>. Check
          your inbox and follow the link to reset your password.
        </Alert>
        <Button fullWidth variant="outlined" onClick={onSwitchToSignIn} sx={{ mt: 3 }}>
          Back to Sign In
        </Button>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleResetPassword} noValidate>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        Reset Password
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enter your email address and we'll send you instructions to reset your password.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Email"
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        margin="normal"
        required
        autoComplete="email"
        autoFocus
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading || !email}
        sx={{ mt: 3, mb: 2 }}
      >
        {loading ? 'Sending...' : 'Send Reset Link'}
      </Button>

      <Typography variant="body2" color="text.secondary" align="center">
        Remember your password?{' '}
        <Link
          component="button"
          type="button"
          onClick={onSwitchToSignIn}
          sx={{ cursor: 'pointer' }}
        >
          Sign in
        </Link>
      </Typography>
    </Box>
  );
};

export default PasswordResetForm;
